import React from 'react';
import { partition } from 'lodash';
import { useTheme } from 'styled-components';
import { AlertNote, CellLoader, Icon, Tooltip } from 'venn-ui-kit';
import { assertExhaustive, capitalizeFirstLetter, Dates, getFormattedFrequency, plural } from 'venn-utils';
import type { SelectedProxy } from './utils';
import type { ProxyTypeEnum } from 'venn-api';
import { type DisabledProxyReason, type FundToBulkProxy } from '../types';
import { getProxyTypeErrorMessageWithType } from './utils';
import { isProxyTypeError } from './proxy-types';
import { cn } from '@/lib/utils';
import {
  type LayeredDetailedProxyMetadata,
  type BulkPickerDetailedProxyMetadata,
  getFirstProxy,
} from '../../../hooks/useProxyDataQuery';
import type { BulkProxyValidationState } from './useProxyErrorMessage';

type ProxyTableData = Pick<
  BulkPickerDetailedProxyMetadata,
  'fundName' | 'unproxiedFrequency' | 'unproxiedStartDate' | 'unproxiedEndDate'
> & {
  proxyId: string | undefined;
};

type InvestmentErrorInfo = Record<string, { disableSave: boolean; errorMessage: string | undefined } | null>;

interface ProxySummaryTableProps {
  investments: FundToBulkProxy[];
  validationState: BulkProxyValidationState;
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  proxyType: ProxyTypeEnum | undefined;
}

export const ProxySummaryTable = ({
  proxyType,
  investments,
  validationState,
  proxyDataByFund,
}: ProxySummaryTableProps) => {
  const investmentsWithProxies = investments.filter(
    (investment) => !!getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const))?.proxyId,
  );
  const hasExistingProxyColumn = Boolean(investmentsWithProxies.length);

  switch (validationState.state) {
    case 'invalidSelection':
      return null;
    case 'loading':
      return (
        <div className="border-t border-gray-300 border-x-0 border-b-0 border-solid bg-white">
          <div className="pt-[16px] px-[24px]">
            <ColumnsHeader hasExistingProxyColumn={hasExistingProxyColumn} />
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            <div className="px-[24px]">
              <Table hasExistingProxyColumn={hasExistingProxyColumn}>
                <SectionHeader selectedProxy={validationState.selectedProxy} />
                {investments.map((investment) => (
                  <Row
                    key={investment.id}
                    proxyData={
                      getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const)) ?? {
                        fundName: investment.name,
                        proxyId: undefined,
                        unproxiedFrequency:
                          'unproxiedFrequency' in investment
                            ? investment.unproxiedFrequency
                            : investment.returnFrequency,
                        unproxiedStartDate: investment.startRange,
                        unproxiedEndDate: investment.endRange,
                      }
                    }
                    status={{ status: 'loading' }}
                    hasExistingProxyColumn={hasExistingProxyColumn}
                  />
                ))}
              </Table>
            </div>
          </div>
        </div>
      );
    case 'ready': {
      const [unableToProxyInvestments, ableToProxyInvestments] = partition(
        investments,
        (investment) => validationState.investmentInfo[investment.id]?.disableSave,
      );

      const ableInvestmentsWithExistingProxies = ableToProxyInvestments.filter(
        (investment) => !!getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const))?.proxyId,
      );

      return (
        <>
          <div className="border-t border-gray-300 border-x-0 border-b-0 border-solid bg-white pb-[12px]">
            <div className="pt-[16px] px-[24px]">
              <ColumnsHeader hasExistingProxyColumn={hasExistingProxyColumn} />
            </div>
            <div className="max-h-[250px] overflow-y-auto">
              <div className="px-[24px]">
                <ProxySummaryTableInternal
                  proxyType={proxyType}
                  ableToProxyInvestments={ableToProxyInvestments}
                  unableToProxyInvestments={unableToProxyInvestments}
                  investmentErrors={validationState.investmentInfo}
                  selectedProxy={validationState.selectedProxy}
                  proxyDataByFund={proxyDataByFund}
                  hasExistingProxyColumn={hasExistingProxyColumn}
                />
              </div>
            </div>
          </div>
          {ableInvestmentsWithExistingProxies.length > 0 && (
            <AlertNote size="small">
              {plural(ableToProxyInvestments.length, {
                1: 'The proxiable investment',
                other: `${ableInvestmentsWithExistingProxies.length} of the ${ableToProxyInvestments.length} proxiable investments`,
              })}{' '}
              {plural(ableInvestmentsWithExistingProxies.length, {
                1: 'has',
                other: 'have',
              })}{' '}
              an existing proxy. Applying the new proxy will replace it.
            </AlertNote>
          )}
        </>
      );
    }
    default:
      throw assertExhaustive(validationState);
  }
};

interface ProxySummaryTableInternalProps {
  proxyType: ProxyTypeEnum | undefined;
  ableToProxyInvestments: FundToBulkProxy[];
  unableToProxyInvestments: FundToBulkProxy[];
  investmentErrors: InvestmentErrorInfo;
  selectedProxy: SelectedProxy;
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  hasExistingProxyColumn: boolean;
}

const addProxyTypeToErrorMessage = (
  errorMessage: DisabledProxyReason | undefined,
  proxyType: ProxyTypeEnum | undefined,
) => {
  if (!errorMessage) return '';
  if (proxyType && isProxyTypeError(errorMessage)) {
    return getProxyTypeErrorMessageWithType(errorMessage, proxyType);
  }
  return errorMessage;
};

const ProxySummaryTableInternal = ({
  proxyType,
  ableToProxyInvestments,
  unableToProxyInvestments,
  investmentErrors,
  selectedProxy,
  proxyDataByFund,
  hasExistingProxyColumn,
}: ProxySummaryTableInternalProps) => {
  return (
    <Table data-testid="qa-proxy-summary-table" hasExistingProxyColumn={hasExistingProxyColumn}>
      {unableToProxyInvestments.length > 0 && <SectionHeader unableToProxy selectedProxy={selectedProxy} />}
      {unableToProxyInvestments.map((investment) => (
        <Row
          key={investment.id}
          proxyData={
            getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const)) ?? {
              fundName: investment.name,
              proxyId: undefined,
              unproxiedFrequency:
                'unproxiedFrequency' in investment ? investment.unproxiedFrequency : investment.returnFrequency,
              unproxiedStartDate: investment.startRange,
              unproxiedEndDate: investment.endRange,
            }
          }
          status={{
            status: 'unableToProxy',
            error: addProxyTypeToErrorMessage(
              investmentErrors[investment.id]?.errorMessage as DisabledProxyReason,
              proxyType,
            ),
          }}
          hasExistingProxyColumn={hasExistingProxyColumn}
        />
      ))}
      {ableToProxyInvestments.length > 0 && <SectionHeader selectedProxy={selectedProxy} />}
      {ableToProxyInvestments.map((investment) => (
        <Row
          key={investment.id}
          proxyData={
            getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const)) ?? {
              fundName: investment.name,
              proxyId: undefined,
              unproxiedFrequency:
                'unproxiedFrequency' in investment ? investment.unproxiedFrequency : investment.returnFrequency,
              unproxiedStartDate: investment.startRange,
              unproxiedEndDate: investment.endRange,
            }
          }
          status={{ status: 'ableToProxy' }}
          hasExistingProxyColumn={hasExistingProxyColumn}
        />
      ))}
    </Table>
  );
};

const ColumnsHeader = ({ hasExistingProxyColumn }: { hasExistingProxyColumn?: boolean }) => (
  <HeaderTable hasExistingProxyColumn={hasExistingProxyColumn} className="items-baseline">
    <div className="text-[14px] leading-none">
      <b>Summary</b>
    </div>
    {hasExistingProxyColumn && (
      <div className="leading-none">
        <b>Has existing proxies?</b>
      </div>
    )}
    <div className="leading-none">
      <b>Frequency</b>
    </div>
    <div className="leading-none">
      <b>Available Dates</b>
    </div>
  </HeaderTable>
);

interface SectionHeaderProps {
  unableToProxy?: boolean;
  selectedProxy: SelectedProxy;
}

const SectionHeader = ({ unableToProxy, selectedProxy }: SectionHeaderProps) => (
  <div className={cn('col-span-full flex gap-[3px] font-bold', unableToProxy ? 'text-venn-error' : 'text-venn-green')}>
    <div>
      {unableToProxy ? 'Unable' : 'Able'} to be proxied with {selectedProxy.name}
    </div>
    <div className="grow h-3/4 border-b border-solid border-x-0 border-t-0 border-gray-300">&nbsp;</div>
  </div>
);

interface RowProps {
  proxyData: ProxyTableData;
  status:
    | {
        status: 'ableToProxy';
      }
    | {
        status: 'unableToProxy';
        error: string;
      }
    | {
        status: 'loading';
      };
  hasExistingProxyColumn?: boolean;
}

const Row = ({
  status,
  proxyData: { unproxiedFrequency, fundName, proxyId, unproxiedStartDate, unproxiedEndDate },
  hasExistingProxyColumn,
}: RowProps) => (
  <>
    <div className="flex gap-[3px] items-end">
      <RowIcon className="self-start" status={status} />
      {fundName}
    </div>
    {hasExistingProxyColumn && <div>{proxyId ? <Icon type="check" /> : null}</div>}

    <div>{capitalizeFirstLetter(getFormattedFrequency(unproxiedFrequency))}</div>
    <div>{`${Dates.toDDMMMYYYY(unproxiedStartDate, unproxiedFrequency)} - ${Dates.toDDMMMYYYY(unproxiedEndDate, unproxiedFrequency)}`}</div>
  </>
);

const RowIcon = ({ status, className }: Pick<RowProps, 'status'> & { className?: string }) => {
  const { Colors } = useTheme();
  switch (status.status) {
    case 'ableToProxy':
      return <Icon type="check" prefix="fal" style={{ color: Colors.Green }} className={className} />;
    case 'unableToProxy':
      return (
        <Tooltip content={status.error} usePortal>
          <Icon type="warning" style={{ color: Colors.Error }} className={className} />
        </Tooltip>
      );
    case 'loading':
      return <CellLoader />;
    default:
      throw assertExhaustive(status);
  }
};

interface TableProps {
  hasExistingProxyColumn?: boolean;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

const Table = ({ hasExistingProxyColumn, children, className, ...props }: TableProps) => (
  <div
    className={cn('grid gap-[6px] py-[6px] text-[12px]', className)}
    style={{
      gridTemplateColumns: hasExistingProxyColumn ? '1fr 150px 100px 175px' : '1fr 100px 175px',
    }}
    {...props}
  >
    {children}
  </div>
);

const HeaderTable = ({ hasExistingProxyColumn, children, className }: Omit<TableProps, 'data-testid'>) => (
  <div
    className={cn('grid gap-[6px] text-[12px]', className)}
    style={{
      gridTemplateColumns: hasExistingProxyColumn ? '1fr 150px 100px 175px' : '1fr 100px 175px',
    }}
  >
    {children}
  </div>
);
